/**
 * Default per-reading rubric settings for the assessment POC (Item 7).
 *
 * Stage 3.8 restructured this from three orthogonal dimensions
 * (completeness / category / overlap) to one match threshold plus two
 * scored dimensions:
 *
 *   - matchThreshold (0–1 fraction): IoU above which a student
 *     highlight counts as "matched" with an expert highlight. The
 *     same threshold gates both dimensions.
 *   - completeness: penalty per missed expert highlight, where
 *     "missed" is graduated — a near-miss earns partial credit (see
 *     assessmentChecks.assessHighlights).
 *   - categoryMismatch: penalty per matched pair whose categories
 *     disagree. Only matched (above-threshold) pairs count.
 *
 * Stored client-side in Redux and serialized into
 * reading_rubrics.criteria as JSON. The `normalizeRubricSettings`
 * helper accepts both the old 3-dimension shape and the new shape so
 * existing data survives the migration.
 */

export interface RubricSettings {
  completeness: { enabled: boolean; penalty: number };
  categoryMismatch: { enabled: boolean; penalty: number };
  /** IoU threshold as a percent (0–100). UI caps it at 95. */
  matchThreshold: number;
  /**
   * Stage 3.8 follow-up: when true, the RubricSection hides the per-
   * dimension knobs and displays a summary of the defaults. Toggling
   * to true *resets* all knob values to the canonical defaults below,
   * so "recommended" really does mean what's stated. Toggling to false
   * reveals the knobs (preserving the current — i.e., default — values
   * as a starting point for customization).
   *
   * Defaults to true for new rubrics; existing rubrics migrate to false
   * to preserve any prior user customization.
   */
  useRecommendedSettings: boolean;
}

export const DEFAULT_MATCH_THRESHOLD = 75;
export const DEFAULT_COMPLETENESS_PENALTY = 5;
export const DEFAULT_CATEGORY_MISMATCH_PENALTY = 3;
/** UI cap. Two annotators rarely select identical spans even on the same text. */
export const MAX_MATCH_THRESHOLD = 95;
/** Visual "typical" range — surfaced as marks on the slider. */
export const TYPICAL_MATCH_THRESHOLD_RANGE: [number, number] = [60, 80];

export const makeDefaultRubricSettings = (): RubricSettings => ({
  completeness: { enabled: true, penalty: DEFAULT_COMPLETENESS_PENALTY },
  categoryMismatch: { enabled: true, penalty: DEFAULT_CATEGORY_MISMATCH_PENALTY },
  matchThreshold: DEFAULT_MATCH_THRESHOLD,
  useRecommendedSettings: true,
});

/**
 * Frozen default settings for selector fallback (stable reference avoids
 * Redux reference-equality warnings when no reading is selected).
 */
export const DEFAULT_RUBRIC_SETTINGS: RubricSettings = Object.freeze({
  completeness: Object.freeze({ enabled: true, penalty: DEFAULT_COMPLETENESS_PENALTY }),
  categoryMismatch: Object.freeze({ enabled: true, penalty: DEFAULT_CATEGORY_MISMATCH_PENALTY }),
  matchThreshold: DEFAULT_MATCH_THRESHOLD,
  useRecommendedSettings: true,
}) as RubricSettings;

/**
 * Parse a stored rubric settings JSON object into the canonical
 * RubricSettings shape. Handles both:
 *   - new shape: { completeness, categoryMismatch, matchThreshold }
 *   - old (pre-3.8) shape: { completeness, categoryMismatch,
 *       overlap: { enabled, threshold } }
 *
 * Missing fields default to the values in DEFAULT_RUBRIC_SETTINGS.
 */
export function normalizeRubricSettings(raw: unknown): RubricSettings {
  const r = (raw ?? {}) as {
    completeness?: { enabled?: boolean; penalty?: number };
    categoryMismatch?: { enabled?: boolean; penalty?: number };
    matchThreshold?: number;
    useRecommendedSettings?: boolean;
    overlap?: { enabled?: boolean; threshold?: number };
  };
  return {
    completeness: {
      enabled: r.completeness?.enabled ?? DEFAULT_RUBRIC_SETTINGS.completeness.enabled,
      penalty: r.completeness?.penalty ?? DEFAULT_RUBRIC_SETTINGS.completeness.penalty,
    },
    categoryMismatch: {
      enabled: r.categoryMismatch?.enabled ?? DEFAULT_RUBRIC_SETTINGS.categoryMismatch.enabled,
      penalty: r.categoryMismatch?.penalty ?? DEFAULT_RUBRIC_SETTINGS.categoryMismatch.penalty,
    },
    matchThreshold:
      typeof r.matchThreshold === "number"
        ? r.matchThreshold
        : typeof r.overlap?.threshold === "number"
        ? r.overlap.threshold
        : DEFAULT_MATCH_THRESHOLD,
    // Existing rubrics that pre-date the toggle land in custom mode so
    // any prior customization stays visible (and editable). New rubrics
    // start in recommended mode via makeDefaultRubricSettings.
    useRecommendedSettings: r.useRecommendedSettings ?? false,
  };
}
